import AttachmentEditor from '@components/AttachmentEditor'
import { Image } from '@models/Image'
import { AvailableLocale } from '@models/Lang'
import { Button, Card, Flex, Form, Space, Typography } from 'antd'
import { ReactNode, useEffect, useState } from 'react'
import { langList } from '../factories/Utils'

const { Title, Text } = Typography

interface Props {
  title: ReactNode
  /** Helper text rendered inside the card, under the draggers. */
  description?: ReactNode
  /** The booklet currently stored for each locale, if any. */
  current: Partial<Record<AvailableLocale, Image | null | undefined>>
  /** Upload the picked files. Only the locales the user touched are passed. */
  onSubmit: (files: Partial<Record<AvailableLocale, File>>) => Promise<unknown>
}

/**
 * One PDF dragger per locale, saved in a single request. Locales left untouched
 * keep the booklet they already have, so partial updates are the normal case.
 */
const BookletUploader = ({ title, description, current, onSubmit }: Props) => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    form.setFieldsValue(
      Object.fromEntries(
        langList.map((lang) => {
          const booklet = current[lang.code]
          return [
            lang.code,
            booklet?.url
              ? [{ name: booklet.name ?? 'booklet.pdf', url: booklet.url }]
              : [],
          ]
        })
      )
    )
  }, [current])

  const onFinish = (values: Record<string, any>) => {
    const files: Partial<Record<AvailableLocale, File>> = {}

    langList.forEach((lang) => {
      // Only entries carrying an originFileObj are newly picked files; the ones
      // pre-filled from `current` have no file attached and must not be resent.
      const picked = values[lang.code]?.[0]?.originFileObj
      if (picked) {
        files[lang.code] = picked
      }
    })

    if (Object.keys(files).length === 0) {
      return
    }

    setIsLoading(true)
    onSubmit(files).finally(() => setIsLoading(false))
  }

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Flex vertical className="w-full" gap={8}>
        <Space className="w-full space-between">
          <Title level={5} className="m-0">
            {title}
          </Title>
          <Button
            style={{ border: 0 }}
            type="primary"
            loading={isLoading}
            htmlType="submit"
          >
            Save
          </Button>
        </Space>

        <Card>
          <Flex vertical>
            <Flex gap={16} wrap>
              {langList.map((lang) => (
                <Flex
                  vertical
                  gap={4}
                  key={lang.code}
                  // minWidth 0: without it an uploaded file's long name sets the
                  // column's minimum width and wraps the locales onto two rows.
                  style={{ flex: '1 1 240px', minWidth: 0 }}
                >
                  <Text strong>{`${lang.flag} ${lang.name}`}</Text>
                  {/*
                    Keep the Form.Item wrapper (no `noStyle`): its margin is what
                    reserves room under the uploaded-file list, which would
                    otherwise sit flush against the card and overlap the next
                    section. `hideLabel` avoids doubling up on the locale label
                    rendered just above.
                  */}
                  <AttachmentEditor
                    formItem={{ name: lang.code, rules: [] }}
                    dragger={{ fileTypes: ['pdf'], multiple: false }}
                    hideLabel
                  />
                </Flex>
              ))}
            </Flex>
            {description && (
              <Card styles={{ body: { padding: 10 } }}>
                <Text type="secondary">{description}</Text>
              </Card>
            )}
          </Flex>
        </Card>
      </Flex>
    </Form>
  )
}

export default BookletUploader
